import { getApp, getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getAuth, signInAnonymously, type Auth } from 'firebase/auth';
import {
  getDatabase,
  goOffline,
  goOnline,
  onChildAdded,
  onDisconnect,
  onValue,
  push,
  ref,
  remove,
  serverTimestamp,
  set,
  update,
  type Database,
  type Unsubscribe,
} from 'firebase/database';
import { appConfig } from '../lib/config';
import { decryptJson, deriveRoomKey, encryptJson, type EncryptedEnvelope } from '../lib/crypto';
import { MAX_DISPLAY_NAME_LENGTH, PROTOCOL_VERSION } from '../lib/constants';
import type { Invite } from '../lib/invite';

interface RoomMeta {
  hostUid: string;
  peerUid?: string;
  protocolVersion: number;
  createdAt: number;
}

export interface JoinRequest {
  uid: string;
  displayName: string;
}

export type SignalPayload =
  | { kind: 'description'; description: RTCSessionDescriptionInit }
  | { kind: 'candidate'; candidate: RTCIceCandidateInit };

function withServerTimestamp<T extends { createdAt: number }>(value: T) {
  return { ...value, createdAt: serverTimestamp() };
}

export class SignalingSession {
  private app?: FirebaseApp;
  private auth?: Auth;
  private database?: Database;
  private key?: CryptoKey;
  private unsubscriptions: Unsubscribe[] = [];
  private meta?: RoomMeta;
  readonly invite: Invite;
  readonly role: 'host' | 'guest';
  uid = '';

  constructor(invite: Invite, role: 'host' | 'guest') {
    this.invite = invite;
    this.role = role;
  }

  async initialize(): Promise<void> {
    this.app = getApps().length ? getApp() : initializeApp(appConfig.firebase);
    if (appConfig.appCheckDebug) window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    if (appConfig.recaptchaSiteKey) {
      try {
        initializeAppCheck(this.app, {
          provider: new ReCaptchaEnterpriseProvider(appConfig.recaptchaSiteKey),
          isTokenAutoRefreshEnabled: true,
        });
      } catch (error) {
        if (!(error instanceof Error) || !error.message.includes('already been initialized')) throw error;
      }
    }
    this.auth = getAuth(this.app);
    const credential = this.auth.currentUser ? null : await signInAnonymously(this.auth);
    this.uid = credential?.user.uid ?? this.auth.currentUser?.uid ?? '';
    if (!this.uid) throw new Error('Anonymous sign-in did not return a user.');
    this.database = getDatabase(this.app);
    goOnline(this.database);
    this.key = await deriveRoomKey(this.invite.secret, this.invite.roomId);
  }

  private requireDatabase(): Database {
    if (!this.database) throw new Error('Signaling has not been initialized.');
    return this.database;
  }

  private requireKey(): CryptoKey {
    if (!this.key) throw new Error('Room encryption has not been initialized.');
    return this.key;
  }

  async createRoom(onJoinRequest: (request: JoinRequest) => void): Promise<void> {
    if (this.role !== 'host') throw new Error('Only a host can create a room.');
    const database = this.requireDatabase();
    const roomRef = ref(database, `rooms/${this.invite.roomId}`);
    const meta: RoomMeta = { hostUid: this.uid, protocolVersion: PROTOCOL_VERSION, createdAt: Date.now() };
    await onDisconnect(roomRef).remove();
    await set(ref(database, `rooms/${this.invite.roomId}/meta`), withServerTimestamp(meta));
    this.meta = meta;
    const unsubscribe = onChildAdded(ref(database, `rooms/${this.invite.roomId}/joinRequests`), async (snapshot) => {
      try {
        const payload = await decryptJson<{ displayName?: string }>(this.requireKey(), snapshot.val() as EncryptedEnvelope);
        onJoinRequest({ uid: snapshot.key!, displayName: (payload.displayName || 'Other person').slice(0, MAX_DISPLAY_NAME_LENGTH) });
      } catch {
        // Invalid and undecryptable join requests are intentionally ignored.
      }
    });
    this.unsubscriptions.push(unsubscribe);
  }

  async requestToJoin(displayName: string, onApproved: (meta: RoomMeta) => void, timeoutMs = 45_000): Promise<void> {
    if (this.role !== 'guest') throw new Error('Only a guest can request to join.');
    const database = this.requireDatabase();
    const metaRef = ref(database, `rooms/${this.invite.roomId}/meta`);
    await new Promise<void>((resolve, reject) => {
      let unsubscribe: Unsubscribe = () => undefined;
      const stop = () => {
        window.clearTimeout(timeout);
        unsubscribe();
        this.unsubscriptions = this.unsubscriptions.filter((item) => item !== unsubscribe);
      };
      const timeout = window.setTimeout(() => {
        stop();
        reject(new Error('The transfer room is not available. Ask the other person to keep PairFerry open and try meeting again.'));
      }, timeoutMs);
      unsubscribe = onValue(metaRef, (snapshot) => {
        const value = snapshot.val() as RoomMeta | null;
        if (!value) return;
        stop();
        this.meta = value;
        resolve();
      }, (error) => {
        stop();
        reject(error);
      });
      this.unsubscriptions.push(unsubscribe);
    });
    const envelope = await encryptJson(this.requireKey(), { displayName: displayName.trim().slice(0, MAX_DISPLAY_NAME_LENGTH) });
    await set(ref(database, `rooms/${this.invite.roomId}/joinRequests/${this.uid}`), withServerTimestamp(envelope));
    await onDisconnect(ref(database, `rooms/${this.invite.roomId}/joinRequests/${this.uid}`)).remove();
    const admissionRef = ref(database, `rooms/${this.invite.roomId}/admissions/${this.uid}`);
    const unsubscribe = onValue(admissionRef, async (snapshot) => {
      if (!snapshot.exists()) return;
      try {
        const approval = await decryptJson<{ approved: boolean; hostUid: string }>(this.requireKey(), snapshot.val() as EncryptedEnvelope);
        if (!approval.approved) return;
        this.meta = { ...this.meta!, hostUid: approval.hostUid, peerUid: this.uid };
        unsubscribe();
        onApproved(this.meta);
      } catch {
        // Keep waiting for a valid approval.
      }
    });
    this.unsubscriptions.push(unsubscribe);
  }

  async approveGuest(uid: string): Promise<void> {
    if (this.role !== 'host' || !this.meta) throw new Error('The room is not ready for approval.');
    const database = this.requireDatabase();
    this.meta = { ...this.meta, peerUid: uid };
    await update(ref(database, `rooms/${this.invite.roomId}/meta`), { peerUid: uid });
    const envelope = await encryptJson(this.requireKey(), { approved: true, hostUid: this.uid });
    await set(ref(database, `rooms/${this.invite.roomId}/admissions/${uid}`), withServerTimestamp(envelope));
  }

  listenForSignals(onSignal: (signal: SignalPayload) => void): void {
    const database = this.requireDatabase();
    const inbox = ref(database, `rooms/${this.invite.roomId}/signals/${this.uid}`);
    const unsubscribe = onChildAdded(inbox, async (snapshot) => {
      try {
        const signal = await decryptJson<SignalPayload>(this.requireKey(), snapshot.val() as EncryptedEnvelope);
        onSignal(signal);
        await remove(snapshot.ref);
      } catch {
        // Ignore malformed, stale, or forged signaling messages.
      }
    });
    this.unsubscriptions.push(unsubscribe);
  }

  async sendSignal(targetUid: string, signal: SignalPayload): Promise<void> {
    const envelope = await encryptJson(this.requireKey(), signal);
    await push(ref(this.requireDatabase(), `rooms/${this.invite.roomId}/signals/${targetUid}`), withServerTimestamp(envelope));
  }

  async finishPairing(): Promise<void> {
    this.unsubscriptions.forEach((unsubscribe) => unsubscribe());
    this.unsubscriptions = [];
    if (this.role === 'host') {
      try {
        await remove(ref(this.requireDatabase(), `rooms/${this.invite.roomId}`));
      } catch {
        // The disconnect hook may already have removed the room.
      }
    }
    goOffline(this.requireDatabase());
  }

  async close(): Promise<void> {
    this.unsubscriptions.forEach((unsubscribe) => unsubscribe());
    this.unsubscriptions = [];
    if (this.database) {
      if (this.role === 'host') await remove(ref(this.database, `rooms/${this.invite.roomId}`)).catch(() => undefined);
      goOffline(this.database);
    }
  }
}
